import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Fade,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from 'react-oidc-context';
import LoginIcon from '@mui/icons-material/Login';

// Styled components for custom design
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  height: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #0755b1 0%, #58dae3 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'fixed',
  top: 0,
  left: 0,
  overflow: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(3),
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  maxWidth: 450,
  width: '100%',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    margin: theme.spacing(1),
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  background: 'linear-gradient(135deg, #0755b1 0%, #58dae3 100%)',
  border: 0,
  color: 'white',
  boxShadow: '0 8px 30px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #0755b1 0%, #58dae3 100%)',
    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 3),
    fontSize: '1rem',
    borderRadius: theme.spacing(2.5),
  },
}));

const LoginPage: React.FC = () => {
  const auth = useAuth();

  const handleSignIn = () => {
    auth.signinRedirect();
  };

  return (
    <LoginContainer>
      <Container
        maxWidth='sm'
        sx={{
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Fade in timeout={800}>
          <LoginPaper elevation={0}>
            <Stack spacing={{ xs: 3, sm: 4 }} alignItems='center'>
              {/* Logo */}
              <Box
                sx={{
                  width: { xs: 80, sm: 100, md: 120 },
                  height: { xs: 80, sm: 100, md: 120 },
                  borderRadius: theme => theme.spacing(2),
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 30px rgba(102, 126, 234, 0.3)',
                  padding: { xs: 1.5, sm: 2 },
                }}
              >
                <img
                  src='/synclogo.png'
                  alt='Sync Labs Logo'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              {/* Welcome Text */}
              <Stack spacing={1} alignItems='center'>
                <Typography
                  variant='h3'
                  fontWeight='bold'
                  sx={{
                    background:
                      'linear-gradient(135deg, #0755b1 0%, #58dae3 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Welcome to Alice
                </Typography>
                <Typography
                  variant='h6'
                  color='text.secondary'
                  sx={{
                    maxWidth: { xs: 280, sm: 350 },
                    lineHeight: 1.5,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Your intelligent care dashboard
                </Typography>
              </Stack>

              {/* Sign In Button */}
              <Box sx={{ width: '100%', mt: { xs: 1, sm: 2 } }}>
                {auth.isLoading ? (
                  <StyledButton disabled fullWidth>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1, color: 'inherit' }}
                    />
                    Signing in...
                  </StyledButton>
                ) : (
                  <StyledButton
                    fullWidth
                    onClick={handleSignIn}
                    startIcon={<LoginIcon />}
                  >
                    Sign In
                  </StyledButton>
                )}
              </Box>

              {/* Footer */}
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  mt: { xs: 1, sm: 2 },
                  opacity: 0.7,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                Powered by Sync Labs
              </Typography>
            </Stack>
          </LoginPaper>
        </Fade>
      </Container>
    </LoginContainer>
  );
};

export default LoginPage;
